/**
 * Visualizes the journey of a job application across multiple stakeholder lanes.
 * Each lane is a horizontal strip; phases are columns showing where each
 * stakeholder is active. Dotted vertical lines connect lanes that Joborigo
 * coordinates information between at each phase.
 *
 * Props:
 *   lanes:    array of { id, label, color } — 2 or 3 lanes supported
 *   phases:   array of { id, label, sublabel } — phase columns
 *   activity: 2D array [phaseIndex][laneIndex] = boolean — which lanes are
 *             active at each phase
 */
export default function JourneyDiagram({ lanes, phases, activity }) {
  // Layout constants in SVG coordinate space
  const VIEWBOX_W = 720;
  const VIEWBOX_H = 280;

  const LEFT_LABEL_WIDTH = 90;
  const RIGHT_PADDING = 20;
  const PHASE_AREA_X = LEFT_LABEL_WIDTH;
  const PHASE_AREA_W = VIEWBOX_W - LEFT_LABEL_WIDTH - RIGHT_PADDING;

  const TOP_PADDING = 24; // for "JOURNEY OF AN APPLICATION" title
  const LANE_HEIGHT = 38;
  const LANE_GAP = 6;
  const FIRST_LANE_TOP = TOP_PADDING;

  const phaseSpacing = PHASE_AREA_W / phases.length;
  const phaseCenters = phases.map(
    (_, i) => PHASE_AREA_X + phaseSpacing * (i + 0.5),
  );

  const laneCenters = lanes.map(
    (_, i) => FIRST_LANE_TOP + LANE_HEIGHT / 2 + i * (LANE_HEIGHT + LANE_GAP),
  );

  const lastLaneBottom =
    FIRST_LANE_TOP + lanes.length * LANE_HEIGHT + (lanes.length - 1) * LANE_GAP;
  const PHASE_LABEL_Y = lastLaneBottom + 22;
  const SUBLABEL_Y = PHASE_LABEL_Y + 14;
  const CAPTION_Y = SUBLABEL_Y + 24;

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      className="w-full h-auto"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Title */}
      <text
        x={0}
        y={14}
        className="fill-brand-violet"
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.2,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        JOURNEY OF AN APPLICATION
      </text>

      {/* Lanes */}
      {lanes.map((lane, i) => {
        const top = FIRST_LANE_TOP + i * (LANE_HEIGHT + LANE_GAP);
        return (
          <g key={lane.id}>
            {/* Lane background */}
            <rect
              x={PHASE_AREA_X - 8}
              y={top}
              width={PHASE_AREA_W + 8}
              height={LANE_HEIGHT}
              rx={6}
              ry={6}
              className="fill-gray-50 dark:fill-white/5 stroke-gray-200 dark:stroke-white/10"
              strokeWidth={1}
            />
            {/* Lane label */}
            <text
              x={0}
              y={top + LANE_HEIGHT / 2 + 4}
              style={{
                fill: lane.color,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              {lane.label}
            </text>
          </g>
        );
      })}

      {/* Vertical connectors between active lanes */}
      {phases.map((_, phaseIdx) => {
        const activeIndices = lanes
          .map((_, laneIdx) => (activity[phaseIdx][laneIdx] ? laneIdx : null))
          .filter((x) => x !== null);
        if (activeIndices.length < 2) return null;
        const topY = laneCenters[activeIndices[0]];
        const bottomY = laneCenters[activeIndices[activeIndices.length - 1]];
        return (
          <line
            key={`connector-${phaseIdx}`}
            x1={phaseCenters[phaseIdx]}
            y1={topY}
            x2={phaseCenters[phaseIdx]}
            y2={bottomY}
            strokeDasharray="4 4"
            strokeWidth={2}
            className="stroke-brand-violet"
            opacity={0.7}
          />
        );
      })}

      {/* Phase dots */}
      {phases.map((_, phaseIdx) =>
        lanes.map((lane, laneIdx) => {
          if (!activity[phaseIdx][laneIdx]) return null;
          return (
            <circle
              key={`dot-${phaseIdx}-${laneIdx}`}
              cx={phaseCenters[phaseIdx]}
              cy={laneCenters[laneIdx]}
              r={6}
              fill={lane.color}
              stroke="white"
              strokeWidth={2}
            />
          );
        }),
      )}

      {/* Phase labels */}
      {phases.map((phase, phaseIdx) => (
        <g key={`label-${phaseIdx}`}>
          <text
            x={phaseCenters[phaseIdx]}
            y={PHASE_LABEL_Y}
            textAnchor="middle"
            className="fill-gray-900 dark:fill-white"
            style={{
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {phase.label}
          </text>
          {phase.sublabel && (
            <text
              x={phaseCenters[phaseIdx]}
              y={SUBLABEL_Y}
              textAnchor="middle"
              className="fill-gray-500 dark:fill-gray-400"
              style={{
                fontSize: 9,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              {phase.sublabel}
            </text>
          )}
        </g>
      ))}

      {/* Caption */}
      <text
        x={VIEWBOX_W / 2}
        y={CAPTION_Y}
        textAnchor="middle"
        className="fill-brand-violet"
        style={{
          fontSize: 9.5,
          fontStyle: "italic",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Dotted lines = Joborigo coordinates information across the loop.
      </text>
    </svg>
  );
}
